<template>
  <p>
    Administra todos los tickets de tus campañas. Si eres administrador verás todos, y si eres
    agente solo verás tus asignados.
    <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
  </p>

  <tickets-table-controls
    style="margin-bottom: 20px"
    :rangeDates="rangeDates"
    :selectedRows="selectedTableRowKeys"
    :downloading="downloading"
    :data="ticketsData"
    @unselectRows="handleUnselectRows"
    @form-fields-completed="handleFormFieldsCompleted"
    @open-advanced-filters-drawer="handleOpenAdvancedFiltersDrawer"
    @open-column-filter-drawer="handleOpenColumnFilterDrawer"
    @update-data="fetchTicketsData"
    @file-download="handleDownload"
    @show-table="handleShowTable"
  />

  <tickets-table
    v-if="showTable"
    :loadingTable="loadingTable"
    :data="ticketsData"
    :columns="columnsItemsVisibles"
    @page-change="handleTablePageChange"
    @open-ticket-history-drawer="handleOpenTicketHistoryDrawer"
    @open-edit-drawer="handleOpenEditDrawer"
    @open-contact-drawer="handleOpenContactDrawer"
    @open-user-modal="handleOpenUserModal"
    @update-data="fetchTicketsData"
  />

  <ticket-drawer-contact
    v-if="ticketHistoryDrawerConfig.visible"
    :item="ticketHistoryDrawerConfig.ticket"
    @onCloseDrawerTicket="handleCloseDrawer"
    :isModuleTicket="isModuleTicket"
  />

  <edit-ticket-drawer
    v-if="editDrawerConfig.visible"
    :visible="editDrawerConfig.visible"
    :ticket="editDrawerConfig.ticket"
    @close-drawer="handleCloseDrawer"
  />

  <tickets-advanced-filters
    v-if="advancedFiltersDrawerVisible"
    :visible="advancedFiltersDrawerVisible"
    :empty="ticketsData.results.length === 0 && !advancedFilters"
    :selectedFilters="advancedFilters"
    @apply-filters="handleApplyAdvancedFilters"
    @clear-filters="handleClearAdvancedFilters"
    @close-drawer="handleCloseDrawer"
  />

  <column-filter
    v-if="columnFilterDrawerVisible"
    v-model:visible="columnFilterDrawerVisible"
    v-model:value="columnsKeysVisibles"
    :columns="columnsPreferences"
    :titleColumns="titleColumns"
    scope="tickets-manager"
    @apply-filters="handleApplyColumnFilters"
    @clear-filters="handleClearColumnFilters"
    @close-drawer="handleCloseDrawer"
  />

  <contact-detail-drawer
    fromSchedule
    v-if="contactDrawerConfig.visible"
    :itemId="contactDrawerConfig.contactId"
    :visible="contactDrawerConfig.visible"
    :campaignName="contactDrawerConfig.campaign"
    :portfolioId="contactDrawerConfig.portfolioId"
    @on-close-drawer="handleCloseDrawer"
  />

  <users-invite-update
    fromTickets
    :record="userModalConfig.record"
    v-if="userModalConfig.visible"
    :visible="userModalConfig.visible"
    @on-close-modal="handleCloseDrawer"
  />
</template>

<script>
import { computed, onMounted, ref, provide, watch } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { debounce } from "lodash";
import { downloadFile } from "@/app/shared/utils/file";
import { SearchOutlined } from "@ant-design/icons-vue";
import { dateToString } from "@/app/shared/utils/dates";
import {
  downloadTicketsWithBasicFilters,
  downloadTicketsWithAdvancedFilters,
} from "@/app/ticketsManager/services";
import TicketsTable from "../tables/TicketsTable.vue";
import TicketsTableControls from "../tables/TicketsTableControls.vue";
import EditTicketDrawer from "../drawers/EditTicketDrawer.vue";
import TicketsAdvancedFilters from "../drawers/TicketsAdvancedFilters.vue";
import ColumnFilter from "../drawers/ColumnFilter.vue";
import ContactDetailDrawer from "@/app/contacts/components/drawers/ContactDetailDrawer.vue";
import UsersInviteUpdate from "@/app/users/components/modals/UsersInviteUpdate.vue";
import TicketDrawerContact from "@/app/contacts/components/drawers/recordTickets/TicketDrawerContact.vue";
import { ticketsTableColumns } from "../../utils/constants";
import { fetchTablePreferences } from "@/app/shared/services";
import { planConfigServices } from "@/app/common/services/PlanConfigServices";

export default {
  components: {
    TicketsTable,
    TicketsTableControls,
    EditTicketDrawer,
    TicketsAdvancedFilters,
    ColumnFilter,
    ContactDetailDrawer,
    UsersInviteUpdate,
    TicketDrawerContact,
  },
  setup() {
    const store = useStore();

    const currentDay = dateToString(new Date()).substring(0, 10);
    const titleColumns =
      "Algunas columnas, como el ticket ID, estado, contacto, fecha de creación, gestionado por y canal son esenciales y no pueden ocultarse.";
    /**** COLUMNS FILTERS ****/
    const columnsPreferences = ref([]);
    const columnsKeysVisibles = ref(
      ticketsTableColumns.filter((el) => el.code != "actions").map((el) => el.code)
    );
    const columnsItemsVisibles = computed(() =>
      ticketsTableColumns.filter(
        (el) => columnsKeysVisibles.value.includes(el.code) || el.code == "actions"
      )
    );
    const handleFetchTablePreferences = async () => {
      let preferences = await fetchTablePreferences("tickets-manager/");
      columnsPreferences.value = preferences;
      if (preferences?.length > 0) {
        if (preferences.filter((el) => el.is_assigned)?.length > 0) {
          columnsKeysVisibles.value = ticketsTableColumns
            .filter((el) => preferences.some((obj) => el.code == obj.code && obj.is_assigned))
            .map((el) => el.code);
        }
      }
    };
    /**** END COLUMNS FILTERS ****/

    /**** validations plan confing ****/
    const user = computed(() => store.getters["getDataUser"]);
    const rangeDates = computed(() => store.getters["getDataPlanConfiguration"]?.messaging_search_range);
    /**** validations plan confing ****/
    
    onMounted(async () => {
      await fetchTicketsData();
      // await store.dispatch("fetchAdvancedFiltersData", {
      //   startDate: `${currentDay} 00:00:00`,
      //   endDate: `${currentDay} 23:59:59`,
      // });
      store.dispatch("setAdvancedFilters", null);
      await handleFetchTablePreferences();
    });

    const showTable = ref(true);
    const loadingTable = ref(false);
    const isModuleTicket = ref(true);
    const ticketsData = computed(() => store.getters["getTickets"]);
    const tablePage = computed(() => store.getters["getTablePage"]);
    const basicFilters = computed(() => store.getters["getTicketsBasicFilters"]);
    const advancedFilters = computed(() => store.getters["getTicketsAdvancedFilters"]);
    const contactsInfo = computed(() => store.getters["getContactsInfoTable"]);

    const handleTablePageChange = async (page) => {
      store.dispatch("setTablePage", page);
      await fetchTicketsData(true);
      console.log(selectedTableRowKeys.value, "selectedTableRowKeys");
    };

    const handleFormFieldsCompleted = async ({ basicFilters, resetAdvancedFilters }) => {
      console.log(basicFilters, "---", resetAdvancedFilters);
      const search = basicFilters?.search ? basicFilters.search?.replace(/^\s*/, "").trim() : null;
      if (/^#\d+$/.test(basicFilters?.search)) {
        basicFilters.search = basicFilters.search.replace(/^\s*#?/, "");
      }
      store.dispatch("setBasicFilters", basicFilters);

      store.dispatch("setTablePage", 1);

      if (advancedFilters.value && resetAdvancedFilters) {
        store.dispatch("setAdvancedFilters", {
          ...advancedFilters.value,
          users: [],
          queues: undefined,
        });
      }

      await fetchTicketsData();
    };

    const handleShowTable = (show) => {
      showTable.value = show;
    };

    const fetchTicketsData = debounce(async function (changePage = false) {
      // loadingTable.value = true;
      store.commit("SET_LOADING", true);
      // resetear los seleccionados si no es un cambio de pagina
      if (!changePage) {
        selectedAll.value = false;
        selectedTableRowKeys.value = [];
        selectedRowsList.value = [];
        selectedAllDataFilters.value = false;
        selectedRowsLastChannel.value = [];
      }
      const filters = { ...basicFilters.value, page: tablePage.value };

      if (tablePage.value === 1 && basicFilters.value) {
        await store.dispatch("fetchAdvancedFiltersData", basicFilters.value);
      }

      if (!basicFilters.value) {
        await store.dispatch("fetchDefaultTickets");
      } else if (!advancedFilters.value) {
        store.dispatch("setAdvancedFilters", null);
        await store.dispatch("fetchTicketsWithBasicFilters", filters);
      } else {
        await store.dispatch("fetchTicketsWithAdvancedFilters", {
          ...basicFilters.value,
          ...advancedFilters.value,
          page: tablePage.value,
        });
      }
      // loadingTable.value = false;
      store.commit("SET_LOADING", false);
    }, 50);

    // Row selection

    const selectedTableRowKeys = ref([]);
    const selectedRowsLastChannel = ref([]);
    const selectedRowsList = ref([]);
    const selectedAll = ref(false);
    const selectedAllDataFilters = ref(false);

    provide("selectedTableRowKeys", selectedTableRowKeys);
    provide("selectedRowsLastChannel", selectedRowsLastChannel);
    provide("selectedRowsList", selectedRowsList);
    provide("selectedAll", selectedAll);
    provide("selectedAllDataFilters", selectedAllDataFilters);

    provide(
      "updateSelectedTableRowKeys",
      (newSelectedRows) => (selectedTableRowKeys.value = newSelectedRows)
    );

    // Actions
    const ticketHistoryDrawerConfig = ref({
      visible: false,
      ticket: {},
    });

    const handleOpenTicketHistoryDrawer = (ticket) => {
      ticketHistoryDrawerConfig.value.visible = true;
      ticketHistoryDrawerConfig.value.ticket = ticket;
    };

    const editDrawerConfig = ref({
      visible: false,
      ticket: {},
    });

    const handleOpenEditDrawer = (ticket) => {
      editDrawerConfig.value.visible = true;
      editDrawerConfig.value.ticket = ticket;
    };

    // Advanced filters
    const advancedFiltersDrawerVisible = ref(false);

    const columnFilterDrawerVisible = ref(false);

    const handleOpenColumnFilterDrawer = () => {
      columnFilterDrawerVisible.value = true;
    };

    const handleOpenAdvancedFiltersDrawer = () => {
      advancedFiltersDrawerVisible.value = true;
    };

    const handleApplyColumnFilters = async (columnFilters) => {
      // store.dispatch("setColumnFilters", columnFilters);
      // store.dispatch("setTablePage", 1);
      // // await fetchTicketsData();
      // handleCloseDrawer();
      message.success("Se han aplicado los filtros correctamente");
    };

    const handleClearColumnFilters = async () => {
      // store.dispatch("setColumnFilters", null);
      // await fetchTicketsData();
    };

    const handleApplyAdvancedFilters = async (advancedFilters) => {
      store.dispatch("setAdvancedFilters", advancedFilters);
      store.dispatch("setTablePage", 1);
      await fetchTicketsData();
      handleCloseDrawer();
      message.success("Se han aplicado los filtros correctamente");
    };

    const handleClearAdvancedFilters = async () => {
      store.dispatch("setAdvancedFilters", null);
      await fetchTicketsData();
    };

    // Contact Drawer
    const contactDrawerConfig = ref({
      visible: false,
      contactId: null,
      campaign: null,
      portfolioId: null,
    });

    const handleOpenContactDrawer = (contactId, campaign, portfolioId) => {
      contactDrawerConfig.value.visible = true;
      contactDrawerConfig.value.contactId = contactId;
      contactDrawerConfig.value.campaign = campaign;
      contactDrawerConfig.value.portfolioId = portfolioId;
    };

    // User Modal
    const usersData = computed(() => store.getters["getUsersData"]);

    const userModalConfig = ref({
      visible: false,
      record: {},
    });

    const handleOpenUserModal = async ({ userName, userId } = {}) => {
      await store.dispatch("fetchUsersData", userName.split(" ").at(0).toLowerCase());
      userModalConfig.value.visible = true;
      userModalConfig.value.record = usersData.value.results.find((u) => u.id === userId);
    };

    const handleCloseDrawer = () => {
      editDrawerConfig.value.visible = false;
      advancedFiltersDrawerVisible.value = false;
      columnFilterDrawerVisible.value = false;
      contactDrawerConfig.value.visible = false;
      userModalConfig.value.visible = false;
      ticketHistoryDrawerConfig.value.visible = false;
    };

    // Download XLSX
    const downloading = ref(false);

    const handleDownload = async () => {
      downloading.value = true;

      try {
        let response = null;

        if (!advancedFilters.value) {
          response = await downloadTicketsWithBasicFilters({ ...basicFilters.value });
        } else {
          response = await downloadTicketsWithAdvancedFilters({
            ...basicFilters.value,
            ...advancedFilters.value,
          });
        }

        downloadFile(response, "Tickets.xlsx", { useResponseFileName: true });
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || "Ha ocurrido un error";

        message.error(errorMessage);
      }

      downloading.value = false;
    };

    const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/crm/gestor-de-tickets-1-0/`;

    return {
      loadingTable,
      ticketsData,
      basicFilters,
      advancedFilters,
      handleTablePageChange,
      showTable,
      handleShowTable,
      handleFormFieldsCompleted,
      fetchTicketsData,
      selectedTableRowKeys,
      ticketHistoryDrawerConfig,
      handleOpenTicketHistoryDrawer,
      editDrawerConfig,
      handleOpenEditDrawer,
      handleCloseDrawer,
      advancedFiltersDrawerVisible,
      columnFilterDrawerVisible,
      handleOpenAdvancedFiltersDrawer,
      handleOpenColumnFilterDrawer,
      handleApplyAdvancedFilters,
      handleClearAdvancedFilters,
      contactDrawerConfig,
      handleOpenContactDrawer,
      userModalConfig,
      handleOpenUserModal,
      handleDownload,
      isModuleTicket,
      downloading,
      contactsInfo,
      DOCS_URL,
      ticketsTableColumns,
      columnsKeysVisibles,
      columnsItemsVisibles,
      handleFetchTablePreferences,
      columnsPreferences,
      titleColumns,
      rangeDates,
    };
  },
};
</script>

<style lang="sass" scoped>
p
  color: $gray-8
  margin-bottom: 12px

section
  display: grid
  justify-items: center
  color: $gray-6
  font-size: 20px
</style>
